import { User } from '../services/authService'
import { Role } from '../services/authService'
import { ROLE_IDS } from './permissions'

/**
 * Format role name by capitalizing and replacing underscores with spaces
 * @param roleName - The role name to format (e.g., "organization_admin")
 * @returns Formatted role name (e.g., "Organization Admin")
 */
export const formatRoleName = (roleName: string): string => {
  return roleName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Filter roles based on the current user's role and permissions
 * - Support/System Admin: Can see all roles
 * - Users with manage:organization_owners permission: Can see all organization roles including owner
 * - Users without manage:organization_owners permission: Cannot see organization owner role
 * - Organization Admin: Can see only member and admin roles
 * @param roles - Array of all available roles
 * @param currentUser - The current logged-in user
 * @returns Filtered array of roles the user can assign
 */
export const getFilteredRoles = (
  roles: Role[],
  currentUser: User | null
): Role[] => {
  if (!currentUser) return []

  const userRoleId = currentUser.roleId
  const userPermissions = currentUser.permissions || []

  // Support and System Admin can see all roles
  if (userRoleId === ROLE_IDS.SUPPORT || userRoleId === ROLE_IDS.SYSTEM_ADMIN) {
    return roles
  }

  // Check if user has permission to manage organization owners
  const canManageOwners = userPermissions.includes('manage:organization_owners')

  // Organization Owner can see only organization roles
  if (userRoleId === ROLE_IDS.ORGANIZATION_OWNER) {
    const organizationRoles = roles.filter(
      (role) =>
        role.id === ROLE_IDS.ORGANIZATION_MEMBER ||
        role.id === ROLE_IDS.ORGANIZATION_ADMIN ||
        role.id === ROLE_IDS.ORGANIZATION_OWNER
    )

    // If user doesn't have manage:organization_owners permission, exclude owner role
    if (!canManageOwners) {
      return organizationRoles.filter(role => role.id !== ROLE_IDS.ORGANIZATION_OWNER)
    }

    return organizationRoles
  }

  // Organization Admin can see only member and admin roles
  if (userRoleId === ROLE_IDS.ORGANIZATION_ADMIN) {
    return roles.filter(
      (role) =>
        role.id === ROLE_IDS.ORGANIZATION_MEMBER ||
        role.id === ROLE_IDS.ORGANIZATION_ADMIN
    )
  }

  // Default: no roles visible for regular members
  return []
}

/**
 * Get filtered roles for display purposes (with formatted names)
 * @param roles - Array of all available roles
 * @param currentUser - The current logged-in user
 * @returns Filtered array of roles with formatted names
 */
export const getFilteredRolesForDisplay = (
  roles: Role[],
  currentUser: User | null
): Array<Role & { displayName: string }> => {
  const filteredRoles = getFilteredRoles(roles, currentUser)
  return filteredRoles.map((role) => ({
    ...role,
    // Use roleName directly if it's already formatted (contains spaces or capitals)
    // Otherwise format it from snake_case
    displayName: role.roleName.includes(' ') || /[A-Z]/.test(role.roleName)
      ? role.roleName
      : formatRoleName(role.roleName)
  }))
}
